import React from 'react';
import { useExpanded, usePagination, useSortBy, useTable } from 'react-table';
import { COLOR } from '../../../../const/color';
import { PerformanceScan } from '../../../../fetch/fetchScanPerformance';
import dayjs from 'dayjs';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import './ScanPerformanceListComponent.scss';
import clsx from 'clsx';

declare interface PerformanceScanPosition extends PerformanceScan {
  position: string;
  preSortScans: number;
  firstScanDate?: string;
}

export function formatPosition(positionInCompetition: number): string {
  if (positionInCompetition > 3) {
    return `${positionInCompetition}`;
  } else if (positionInCompetition === 1) {
    return `${positionInCompetition}st 🚀`;
  } else if (positionInCompetition === 2) {
    return `${positionInCompetition}nd 🏇`;
  } else if (positionInCompetition === 3) {
    return `${positionInCompetition}rd 🐇`;
  }
}
const PreformanceColor = {
  Winner: '#661AFF',
  Second: '#6A4DFF',
  Third: '#7E73FF',
  WHITE: '#ffffff',
};

function isEvenNumber(nr: number): boolean {
  const isEven: boolean = nr % 2 === 0;
  return isEven;
}

function colorRowBasedOnRating(positionIndex: number): {
  backgroundColor: string;
  color: string;
} {
  if (positionIndex >= 3) {
    if (isEvenNumber(positionIndex)) {
      return { backgroundColor: COLOR.WHITE, color: COLOR.BLACK };
    } else {
      return { backgroundColor: COLOR.WHITE_GRAY, color: COLOR.BLACK };
    }
  } else if (positionIndex === 2) {
    return {
      backgroundColor: PreformanceColor.Third,
      color: COLOR.WHITE,
    };
  } else if (positionIndex === 1) {
    return {
      backgroundColor: PreformanceColor.Second,
      color: COLOR.WHITE,
    };
  } else if (positionIndex === 0) {
    return {
      backgroundColor: PreformanceColor.Winner,
      color: COLOR.WHITE,
    };
  }
}

function ShowTable({
  columns: userColumns,
  data,
}: {
  columns;
  data: PerformanceScan[];
}): JSX.Element {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable(
      {
        columns: userColumns,
        data,
        initialState: {
          pageIndex: 0,
          pageSize: 200,
        },
      },
      useSortBy,
      useExpanded,
      usePagination
    );

  return (
    <TableContainer>
      <Table stickyHeader {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup, index) => (
            <TableRow key={index} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any, columnIndex: number) => (
                <TableCell
                  key={columnIndex}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render('Header')}
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? ' 🔽'
                        : ' 🔼'
                      : ''}
                  </span>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <TableRow
                key={i}
                {...row.getRowProps({
                  style: colorRowBasedOnRating(i),
                })}
              >
                {row.cells.map(
                  (
                    cell: {
                      getCellProps: () => JSX.IntrinsicAttributes &
                        React.ClassAttributes<HTMLTableDataCellElement> &
                        React.TdHTMLAttributes<HTMLTableDataCellElement>;
                      render: (arg0: string) => React.ReactNode;
                    },
                    cellIndex: number
                  ) => {
                    return (
                      <TableCell
                        key={cellIndex}
                        style={{ backgroundColor: 'inherit', color: 'inherit' }}
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    );
                  }
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function ScanPerformanceListComponent({
  performanceScanList,
  isLeaderBoard,
}: {
  performanceScanList: PerformanceScan[];
  isLeaderBoard: boolean;
}): JSX.Element {
  const columns = React.useMemo(
    () => [
      {
        Header: 'No',
        accessor: 'position',
      },
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'start',
        accessor: 'firstScanDate',
      },
      {
        Header: 'Presort',
        accessor: 'preSortScans',
      },
      {
        Header: 'cage',
        accessor: 'nrOfCompletedScans',
      },
    ],
    []
  );

  const newList: PerformanceScanPosition[] = performanceScanList.map(
    (obj, index) => {
      const positionInCompetition = index + 1;
      return {
        ...obj,
        name: `${obj.firstName} ${obj.lastName}`,
        position: formatPosition(positionInCompetition),
        preSortScans: obj.nrOfFirstScans - obj.nrOfCompletedScans,
        firstScanDate: dayjs(obj.firstScan).format('HH:mm'),
      };
    }
  );

  const data = React.useMemo(() => newList, [newList]);

  return (
    <div
      className={clsx(
        isLeaderBoard
          ? 'ScanPerformanceListComponent-isLeaderboard'
          : 'ScanPerformanceListComponent'
      )}
    >
      <ShowTable columns={columns} data={data} />
    </div>
  );
}
